import MenuIcon from '@mui/icons-material/Menu';
import AddModeratorOutlinedIcon from '@mui/icons-material/AddModeratorOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import Grow from '@mui/material/Grow';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import React, { useEffect, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Route } from 'src/components/common/Sidebar/const';
import useAppSelector from 'src/hooks/Redux/useAppSelector';
import { useUserLoginLogout } from 'src/hooks/UserLoginLogout/UserLoginLogout';
import { selectCurrentUser } from 'src/redux/slices/loggedUserSlice';
import styles from './hamburgerMenuMobile.module.scss';

const LANGUAGE_STORAGE_KEY = 'webapp.language';

type MenuListCompositionProps = {
  routes: Route[];
  isAuthenticated?: boolean;
  onLoginClick?: () => void;
};

export default function MenuListComposition({
  routes,
  isAuthenticated = false,
  onLoginClick,
}: MenuListCompositionProps) {
  const [open, setOpen] = useState(false);
  const [shouldRestoreFocus, setShouldRestoreFocus] = useState(true);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const { logout } = useUserLoginLogout();
  const { i18n, t } = useTranslation();
  const loggedInUser = useAppSelector(selectCurrentUser);
  const visibleRoutes =
    loggedInUser?.role === 'ADMIN'
      ? routes
      : routes.filter((route) => route.adminOnly !== true);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isOth360InviteFlowRoute =
    location.pathname.includes('/oth/360/results') ||
    location.pathname.includes('/oth/360/form');
  const selectedLanguage = ['no', 'da', 'en'].includes(
    i18n.resolvedLanguage || ''
  )
    ? (i18n.resolvedLanguage as 'no' | 'da' | 'en')
    : ['no', 'da', 'en'].includes(i18n.language || '')
      ? (i18n.language as 'no' | 'da' | 'en')
      : 'no';
  const languageOptions = [
    { value: 'no', label: 'Norsk', countryCode: 'NO' },
    { value: 'da', label: 'Dansk', countryCode: 'DK' },
    { value: 'en', label: 'English', countryCode: 'GB' },
  ] as const;
  const selectedLanguageOption =
    languageOptions.find((option) => option.value === selectedLanguage) ||
    languageOptions[0];
  const mobileLanguageSectionStyle: React.CSSProperties = {
    padding: '14px 16px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    minWidth: '250px',
  };
  const mobileLanguageLabelStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    color: '#5f6270',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: 1.2,
  };
  const mobileLanguageTriggerStyle: React.CSSProperties = {
    width: '100%',
    minWidth: '180px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '12px 14px',
    border: '2px solid #d1d5db',
    borderRadius: '10px',
    background: '#fff',
    color: '#111',
    fontSize: '16px',
    fontWeight: 700,
    textAlign: 'left',
    cursor: 'pointer',
  };
  const mobileLanguageListStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid #ececec',
    borderRadius: '10px',
    overflow: 'hidden',
    background: '#fff',
  };
  const mobileLanguageListItemStyle = (
    isActive: boolean,
    isLast: boolean
  ): React.CSSProperties => ({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '12px 14px',
    border: 0,
    borderBottom: isLast ? 'none' : '1px solid #ececec',
    background: isActive ? '#f8f5f5' : '#fff',
    color: '#111',
    fontSize: '16px',
    fontWeight: 500,
    textAlign: 'left',
    cursor: 'pointer',
  });
  const mobileLanguageOptionStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent, route?: Route) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    if (route && route.path) {
      navigate(route.path);
    }

    setOpen(false);
    setIsLanguageMenuOpen(false);
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  const handleSignOut = async () => {
    await logout();
  };

  const handleLanguageChange = (language: 'no' | 'da' | 'en') => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
      document.documentElement.lang = language;
    }
    void i18n.changeLanguage(language);
    setIsLanguageMenuOpen(false);
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false && shouldRestoreFocus) {
      anchorRef.current?.focus();
    }

    if (!open && !shouldRestoreFocus) {
      setShouldRestoreFocus(true);
    }

    prevOpen.current = open;
  }, [open, shouldRestoreFocus]);

  const getRouteIcon = (route: Route, isActive: boolean) => {
    if (route.title === 'Admin Panel') {
      return (
        <AddModeratorOutlinedIcon
          sx={{ color: isActive ? '#F0544F' : 'inherit' }}
        />
      );
    }

    if (isActive && route.activeIcon) {
      return route.activeIcon;
    }

    return route.icon;
  };

  const isRouteActive = (route: Route) => {
    if (route.children?.length) {
      return (
        location.pathname === route.path ||
        route.children.some((child) => location.pathname === child.path)
      );
    }

    return location.pathname === route.path;
  };

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <div>
        <div
          ref={anchorRef}
          id="composition-button"
          aria-controls={open ? 'composition-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        >
          <MenuIcon />
        </div>
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          placement="bottom-end"
          sx={{ zIndex: 2 }}
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === 'bottom-end' ? 'right top' : 'right bottom',
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={(event) => handleClose(event)}>
                  <MenuList
                    autoFocusItem={open}
                    id="composition-menu"
                    aria-labelledby="composition-button"
                    onKeyDown={handleListKeyDown}
                  >
                    {visibleRoutes.map((route) => {
                      const isActive = isRouteActive(route);

                      return (
                        <MenuItem
                          key={route.title}
                          onClick={(event) => handleClose(event, route)}
                          className={styles.menuItem}
                          style={{
                            color: isActive ? '#F0544F' : 'inherit',
                            fontWeight: isActive ? 700 : 400,
                          }}
                        >
                          {route.icon && (
                            <ListItemIcon
                              className={styles.menuIcon}
                              sx={{ color: 'inherit' }}
                            >
                              {getRouteIcon(route, isActive)}
                            </ListItemIcon>
                          )}
                          {route.title}
                        </MenuItem>
                      );
                    })}
                    {isOth360InviteFlowRoute && (
                      <>
                        <Divider />
                        <div
                          className={styles.mobileLanguageSection}
                          style={mobileLanguageSectionStyle}
                        >
                          <div
                            className={styles.mobileLanguageLabel}
                            style={mobileLanguageLabelStyle}
                          >
                            <span
                              className={styles.mobileLanguageLabelEmoji}
                              aria-hidden="true"
                            >
                              🌍
                            </span>
                            {t('main.language.label')}:
                          </div>
                          <button
                            type="button"
                            className={styles.mobileLanguageTrigger}
                            aria-label={t('main.language.label')}
                            aria-expanded={isLanguageMenuOpen}
                            style={mobileLanguageTriggerStyle}
                            onClick={() =>
                              setIsLanguageMenuOpen((prevOpen) => !prevOpen)
                            }
                          >
                            <span
                              className={styles.mobileLanguageOption}
                              style={mobileLanguageOptionStyle}
                            >
                              <ReactCountryFlag
                                className={styles.mobileLanguageFlag}
                                countryCode={selectedLanguageOption.countryCode}
                                svg
                                aria-hidden="true"
                              />
                              <span>{selectedLanguageOption.label}</span>
                            </span>
                            {isLanguageMenuOpen ? (
                              <KeyboardArrowUpIcon />
                            ) : (
                              <KeyboardArrowDownIcon />
                            )}
                          </button>
                          {isLanguageMenuOpen && (
                            <div
                              className={styles.mobileLanguageList}
                              style={mobileLanguageListStyle}
                            >
                              {languageOptions.map((option, index) => (
                                <button
                                  key={option.value}
                                  type="button"
                                  className={`${styles.mobileLanguageListItem} ${
                                    option.value === selectedLanguage
                                      ? styles.mobileLanguageListItemActive
                                      : ''
                                  }`}
                                  style={mobileLanguageListItemStyle(
                                    option.value === selectedLanguage,
                                    index === languageOptions.length - 1
                                  )}
                                  onClick={() =>
                                    handleLanguageChange(option.value)
                                  }
                                >
                                  <span
                                    className={styles.mobileLanguageOption}
                                    style={mobileLanguageOptionStyle}
                                  >
                                    <ReactCountryFlag
                                      className={styles.mobileLanguageFlag}
                                      countryCode={option.countryCode}
                                      svg
                                      aria-hidden="true"
                                    />
                                    <span>{option.label}</span>
                                  </span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                    <Divider />
                    {isAuthenticated ? (
                      <MenuItem
                        onClick={(event) => {
                          handleClose(event);
                          handleSignOut();
                        }}
                        className={styles.menuItem}
                        style={{
                          color: 'inherit',
                          fontWeight: 400,
                        }}
                      >
                        <ListItemIcon
                          className={styles.menuIcon}
                          sx={{ color: 'inherit' }}
                        >
                          <LogoutOutlinedIcon />
                        </ListItemIcon>
                        Logout
                      </MenuItem>
                    ) : (
                      <MenuItem
                        onClick={(event) => {
                          setShouldRestoreFocus(false);
                          handleClose(event);
                          window.setTimeout(() => {
                            onLoginClick?.();
                          }, 200);
                        }}
                        style={{
                          color: 'inherit',
                          fontWeight: 'normal',
                        }}
                      >
                        Log in
                      </MenuItem>
                    )}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </div>
    </Stack>
  );
}
